/**
 * Export GTFS Modal
 *
 * Single modal that drives the whole GTFS export off the Fixed Routes Data tab:
 * route selection (all by default), feed/agency metadata, a fallback service
 * calendar, a live pre-export summary, and the download. Per-route service days
 * + headsign/description are edited in the route editor (Route Details tab);
 * the fallback calendar here only applies to routes that leave them unset.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@liftango/ui-icons';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import {
  buildGtfsFeed,
  writeGtfsZip,
  type GtfsExportOptions,
} from '../../utils/fixedRoutes/gtfsExporter';
import { downloadBlob } from '../../utils/fixedRoutes/exportHelpers';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { formatDateAsWallTime } from '../../utils/dateTimeUtils';
import type { ServiceDaysMask } from '../../types/fixedRoutes';

interface ExportGtfsModalProps {
  open: boolean;
  onClose: () => void;
}

const DAY_FIELDS: Array<{ key: keyof ServiceDaysMask; label: string }> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

const WEEKDAY_MASK: ServiceDaysMask = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
};

/** today/now is fine for a version/filename stamp; service dates use wall-time helpers. */
const wallTimeStamp = (): string => formatDateAsWallTime(new Date());

/** Add one year to a YYYY-MM-DD string without Date parsing (timezone-safe). */
const plusOneYear = (isoDate: string): string => {
  const [y, m, d] = isoDate.split('-');
  return `${String(Number(y) + 1).padStart(4, '0')}-${m}-${d}`;
};

const slugify = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'feed';

export function ExportGtfsModal({ open, onClose }: ExportGtfsModalProps) {
  const { t } = useTranslation('fixedRoutes');
  const { stops, routes, trafficMultiplier } = useFixedRoutes();

  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  );

  const [selectedRouteIds, setSelectedRouteIds] = useState<Set<string>>(
    () => new Set(routes.map((r) => r.id)),
  );
  const [feedName, setFeedName] = useState('Atlas');
  const [agencyName, setAgencyName] = useState('Atlas');
  const [agencyUrl, setAgencyUrl] = useState('https://liftango.com');
  const [agencyLang, setAgencyLang] = useState('en');
  const [days, setDays] = useState<ServiceDaysMask>(WEEKDAY_MASK);
  const [startDate, setStartDate] = useState(DEFAULT_SIMULATION_DATE);
  const [endDate, setEndDate] = useState(() => plusOneYear(DEFAULT_SIMULATION_DATE));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options: GtfsExportOptions = useMemo(
    () => ({
      selectedRouteIds: Array.from(selectedRouteIds),
      defaultServiceDays: days,
      defaultServiceDateRange: { startDate, endDate },
      meta: {
        feedName,
        agencyName,
        agencyUrl,
        agencyTimezone: timezone,
        agencyLang,
        feedVersion: wallTimeStamp().slice(0, 19),
      },
    }),
    [selectedRouteIds, days, startDate, endDate, feedName, agencyName, agencyUrl, agencyLang, timezone],
  );

  // Live preview/validation. buildGtfsFeed is cheap + pure, so recompute freely.
  const preview = useMemo(
    () => buildGtfsFeed({ stops, routes, trafficMultiplier }, options),
    [stops, routes, trafficMultiplier, options],
  );

  const toggleRoute = (id: string) => {
    setSelectedRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = selectedRouteIds.size === routes.length && routes.length > 0;
  const toggleAll = () =>
    setSelectedRouteIds(allSelected ? new Set() : new Set(routes.map((r) => r.id)));

  const setDay = (key: keyof ServiceDaysMask, value: boolean) =>
    setDays((prev) => ({ ...prev, [key]: value }));

  const canDownload = selectedRouteIds.size > 0 && preview.counts.routes > 0 && !busy;

  const handleDownload = async () => {
    const start = Date.now();
    setBusy(true);
    setError(null);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_start, {
      export_type: 'gtfs',
      export_source: 'fixed_routes',
      item_count: preview.counts.routes,
      timestamp: new Date().toISOString(),
    });
    try {
      const blob = await writeGtfsZip(preview.feed);
      const filename = `gtfs-${slugify(feedName)}-${wallTimeStamp().slice(0, 10)}.zip`;
      downloadBlob(blob, filename);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_complete, {
        export_type: 'gtfs',
        export_source: 'fixed_routes',
        item_count: preview.counts.routes,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_fail, {
        feature_area: 'export',
        error_category: 'export_error',
        error_code: err instanceof Error ? err.name : 'unknown_error',
        retryable: true,
        export_type: 'gtfs',
        export_source: 'fixed_routes',
        item_count: preview.counts.routes,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon name="bus" size={20} />
          {t('gtfsExport.title', 'Export GTFS Feed')}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'gtfsExport.description',
              'Generate a spec-compliant GTFS .zip from your fixed routes. Per-route service days and headsigns come from each route’s Route Details; the calendar below is the fallback for routes that don’t set their own.',
            )}
          </Typography>

          {/* Route selection */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon name="route" size={18} />
                <Typography variant="subtitle2">
                  {t('gtfsExport.routes', 'Routes to include')}
                </Typography>
              </Box>
              <Button size="small" onClick={toggleAll} disabled={routes.length === 0}>
                {allSelected ? t('gtfsExport.selectNone', 'Select none') : t('gtfsExport.selectAll', 'Select all')}
              </Button>
            </Box>
            <List dense sx={{ maxHeight: 180, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {routes.map((route) => (
                <ListItem key={route.id} disablePadding>
                  <ListItemButton onClick={() => toggleRoute(route.id)} dense>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start" checked={selectedRouteIds.has(route.id)} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText primary={route.name} secondary={route.shortName} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider />

          {/* Agency / feed metadata */}
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="flag" size={18} />
              <Typography variant="subtitle2">{t('gtfsExport.agency', 'Agency & feed')}</Typography>
            </Box>
            <TextField
              label={t('gtfsExport.feedName', 'Feed name')}
              value={feedName}
              onChange={(e) => setFeedName(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label={t('gtfsExport.agencyName', 'Agency name')}
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label={t('gtfsExport.agencyUrl', 'Agency URL')}
              value={agencyUrl}
              onChange={(e) => setAgencyUrl(e.target.value)}
              size="small"
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('gtfsExport.agencyLang', 'Language')}
                value={agencyLang}
                onChange={(e) => setAgencyLang(e.target.value)}
                size="small"
                sx={{ width: 120 }}
              />
              <TextField
                label={t('gtfsExport.timezone', 'Timezone (auto)')}
                value={timezone}
                size="small"
                fullWidth
                disabled
                helperText={t('gtfsExport.timezoneHelp', 'Derived automatically; not editable')}
              />
            </Box>
          </Stack>

          <Divider />

          {/* Fallback calendar */}
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="clock" size={18} />
              <Typography variant="subtitle2">
                {t('gtfsExport.fallbackCalendar', 'Fallback service calendar')}
              </Typography>
            </Box>
            <FormGroup row>
              {DAY_FIELDS.map(({ key, label }) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox size="small" checked={days[key]} onChange={(e) => setDay(key, e.target.checked)} />
                  }
                  label={label}
                />
              ))}
            </FormGroup>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="date"
                label={t('gtfsExport.startDate', 'Start date')}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label={t('gtfsExport.endDate', 'End date')}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Stack>

          <Divider />

          {/* Summary + warnings */}
          <Alert severity="info" icon={<Icon name="circle-info" size={20} />}>
            <AlertTitle>{t('gtfsExport.summaryTitle', 'Export summary')}</AlertTitle>
            {t('gtfsExport.summary', {
              defaultValue: '{{routes}} routes · {{trips}} trips · {{stops}} stops · {{calendars}} calendars · {{agencies}} agencies',
              routes: preview.counts.routes,
              trips: preview.counts.trips,
              stops: preview.counts.stops,
              calendars: preview.counts.calendars,
              agencies: preview.counts.agencies,
            })}
          </Alert>

          {preview.skippedRoutes.length > 0 && (
            <Alert severity="warning" icon={<Icon name="triangle-exclamation" size={20} />}>
              <AlertTitle>
                {t('gtfsExport.skippedTitle', { defaultValue: '{{count}} route(s) skipped', count: preview.skippedRoutes.length })}
              </AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {preview.skippedRoutes.map((s) => (
                  <li key={s.routeId}>
                    {s.routeName}: {s.reason}
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {error && (
            <Alert severity="error" icon={<Icon name="triangle-exclamation" size={20} />}>
              <AlertTitle>{t('gtfsExport.errorTitle', 'Export failed')}</AlertTitle>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon name="file-arrow-down" size={20} />}
          onClick={handleDownload}
          disabled={!canDownload}
        >
          {t('gtfsExport.download', 'Download GTFS')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
